import React, { useEffect, useState } from "react";
import "./PersonProfilePicture.scss";
import axios from "axios";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { TaskType } from "src/types";
import { RootState } from "src/redux/reducers";
import creatorPlaceholder from "../../../images/creator-placeholder.svg";
import placeholderImage from "../../../images/placeholder-image.svg";
import publicationPlaceholder from "../../../images/publication-placeholder-grey.svg";
import { API_URLS } from "../../../utils/API_URLS";
import { showToast } from "../../methods/showToast";
import { getImageSource } from "../../../utils/methods";
import ImageWithOptionsOverlay from "../ImageWithOptionsOverlay/ImageWithOptionsOverlay";

type Props = {
  item: any;
  src?: string | null;
  updateData?: () => void;
  parentLoading?: boolean;
};

function PersonProfilePicture(props: Props) {
  const { src, item, updateData, parentLoading } = props;

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const [coverImageUrl, setCoverImageUrl] = useState<null | string>(
    src || null,
  );
  const [coverImageFile, setCoverImageFile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setCoverImageUrl(src || null);
  }, [src]);

  const setNewImage = (file: any) => {
    if (!file) {
      return;
    }

    let fileExtension = file.name.split(".");
    fileExtension = fileExtension[fileExtension.length - 1];
    const allowedTypes = ["png", "jpg", "jpeg"];

    if (!file) {
      return;
    }

    if (!allowedTypes.includes(fileExtension.toLowerCase())) {
      showToast("error", "Error", "Invalid file type.");
      return;
    }

    const size = file.size / parseInt((1000 * 1000).toFixed(2), 10);
    if (size > 10) {
      showToast("error", "Error", "File is too large.");
    } else {
      setCoverImageFile(file);
      setLoading(true);
    }
  };

  useEffect(() => {
    if (coverImageFile) {
      uploadMedia();
    }
  }, [coverImageFile]);

  useEffect(() => {
    const src = getImageSource(item?.cover, "tiny");

    if (src && src !== coverImageUrl) {
      setCoverImageUrl(src);
    }
  }, [item?.cover?.thumbnails]);

  const uploadMedia = () => {
    const wsTaskUuid = item.projectId || item.taskId;

    setLoading(true);

    setCoverImageUrl(null);

    axios
      .get(
        `${API_URLS.presignedRequestForTaskCover}?wsTaskUuid=${wsTaskUuid}&wsWorkspaceUuid=${activeWorkspaceUuid}&name=cover`,
      )
      .then(function (response) {
        const {
          requestInputs,
          requestAttributes: { action },
        } = response.data.content;

        axios
          .post(action, getFormDataToUploadS3(coverImageFile, requestInputs))
          .then(function (response) {
            if (response.status === 201) {
              const parentUuid = item.projectId || item.taskId;
              const requestInputsKeyArray = requestInputs.key.split("/");
              const realName =
                requestInputsKeyArray[requestInputsKeyArray.length - 1];

              axios
                .post(API_URLS.createAsset, {
                  wsWorkspaceUuid: activeWorkspaceUuid,
                  parentUuid,
                  parentType: "ws_task",
                  name: coverImageFile.name,
                  realName,
                  mimeType: coverImageFile.type,
                  size: coverImageFile.size,
                  isUpdatingCoverTask: true,
                })
                .then(function (response) {
                  if (updateData) {
                    updateData();
                  }
                  setLoading(false);
                  setCoverImageUrl(response.data.content.url);
                });
            }
          })
          .catch(function (error) {
            console.error(error.response?.data?.content?.errors);
            setLoading(false);
          });
      })
      .catch(function () {
        setLoading(false);
      });
  };

  const getFormDataToUploadS3 = (file: File, requestInputs: any) => {
    const formData = new FormData();

    formData.set("key", requestInputs.key);
    formData.set("acl", requestInputs.acl);
    formData.set("X-Amz-Credential", requestInputs["X-Amz-Credential"]);
    formData.set("X-Amz-Algorithm", requestInputs["X-Amz-Algorithm"]);
    formData.set("X-Amz-Date", requestInputs["X-Amz-Date"]);
    formData.set("Policy", requestInputs.Policy);
    formData.set("X-Amz-Signature", requestInputs["X-Amz-Signature"]);
    formData.set("success_action_status", requestInputs.success_action_status);
    formData.set("Content-Type", file.type);
    formData.set("file", file);

    return formData;
  };

  const removeCoverPhoto = () => {
    axios
      .delete(
        `${API_URLS.deleteAsset}/${activeWorkspaceUuid}/${item.cover?.id}`,
      )
      .then(function () {
        setCoverImageUrl(null);
        if (updateData) {
          updateData();
        }
      });
  };

  const shape =
    item.taskType === TaskType.Content || item.taskType === TaskType.Publication
      ? "square"
      : "circle";

  const getPlaceholderSrc = () => {
    if (item.taskType === TaskType.Creator) {
      return creatorPlaceholder;
    }

    if (item.taskType === TaskType.Publication) {
      return publicationPlaceholder;
    }
    return placeholderImage;
  };

  return (
    <span
      className={classNames("person-profile-picture", {
        "person-profile-picture--square":
          item.taskType === TaskType.Content ||
          item.taskType === TaskType.Publication,
      })}
    >
      <ImageWithOptionsOverlay
        className="person-profile-picture__photo"
        imageSrc={coverImageUrl}
        downloadImageSrc={item?.cover?.downloadUrl}
        downloadFileName={item?.title}
        fallbackImageSrc={getPlaceholderSrc()}
        shape={shape}
        loading={loading || parentLoading}
        uploadFunction={setNewImage}
        removeFunction={item?.cover?.isRemovable ? removeCoverPhoto : undefined}
      />
    </span>
  );
}

export default PersonProfilePicture;
